import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Calendar, Clock, LogOut, CalendarOff, Plus, Check, X, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { fmtDateMed, fmtDateFull, fmtRange, fmtTime } from "../lib/dates";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";

const StaffDashboard = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaveDialog, setLeaveDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    leave_type: "annual_leave",
    start_date: null,
    end_date: null,
    reason: ""
  });

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/staff/me`);
      setStaff(res.data);
    } catch {
      navigate("/staff");
    }
  }, [navigate]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const fetchData = useCallback(async () => {
    try {
      const [schedRes, leaveRes] = await Promise.all([
        axios.get(`${API}/staff/schedule`),
        axios.get(`${API}/staff/leave-requests`)
      ]);
      setSchedules(schedRes.data);
      setLeaveRequests(leaveRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (staff) fetchData();
  }, [staff, fetchData]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/staff/auth/logout`, {});
    } catch (e) { console.warn("Logout request failed (continuing):", e?.response?.status || e); }
    localStorage.removeItem("staff_name");
    navigate("/staff");
  };

  const handleSubmitLeave = async (e) => {
    e.preventDefault();
    if (!leaveForm.start_date || !leaveForm.end_date) {
      toast.error("Please select dates"); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/staff/leave-request`, {
        leave_type: leaveForm.leave_type,
        start_date: format(leaveForm.start_date, "yyyy-MM-dd"),
        end_date: format(leaveForm.end_date, "yyyy-MM-dd"),
        reason: leaveForm.reason
      });
      toast.success("Leave request submitted!");
      setLeaveDialog(false);
      setLeaveForm({ leave_type: "annual_leave", start_date: null, end_date: null, reason: "" });
      fetchData();
    } catch (err) {
      toast.error("Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === "approved") return "bg-emerald-50 text-emerald-700";
    if (status === "rejected") return "bg-red-50 text-red-700";
    return "bg-amber-50 text-amber-700";
  };

  const leaveTypeLabels = {
    rostered_day_off: "Rostered Day Off",
    annual_leave: "Annual Leave",
    sick_leave: "Sick Leave",
    other: "Other"
  };

  if (loading || !staff) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold tracking-tight text-zinc-900">
              roster<span className="text-[#002FA7]">.ai</span>
            </h1>
            <p className="text-xs text-zinc-500">Staff Portal</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-medium text-zinc-900">{staff.name}</p>
              <p className="text-xs text-zinc-500">{staff.email}</p>
            </div>
            <button onClick={handleLogout} className="p-2 hover:bg-zinc-100 rounded transition-colors"
              data-testid="staff-logout-btn">
              <LogOut className="w-4 h-4 text-zinc-500" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Actions */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl tracking-tight font-bold text-zinc-900">My Schedule</h2>
          <Button onClick={() => setLeaveDialog(true)}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white"
            data-testid="request-leave-btn">
            <CalendarOff className="w-4 h-4 mr-2" /> Request Leave
          </Button>
        </div>

        {/* Schedule */}
        {schedules.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center">
            <Calendar className="w-10 h-10 text-zinc-300 mx-auto mb-4" />
            <h3 className="text-sm font-medium text-zinc-900 mb-1">No published schedules</h3>
            <p className="text-xs text-zinc-500">Your roster hasn't been published yet</p>
          </div>
        ) : (
          schedules.map((schedule) => (
            <div key={schedule.roster_id} className="bg-white border border-zinc-200 overflow-hidden" data-testid={`schedule-${schedule.roster_id}`}>
              <div className="p-4 border-b border-zinc-200 bg-zinc-50">
                <h3 className="text-base font-medium text-zinc-900">{schedule.roster_name}</h3>
                <p className="text-xs text-zinc-500 mt-0.5">{fmtRange(schedule.start_date, schedule.end_date)}</p>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 text-left py-3 px-4">Date</th>
                    <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 text-left py-3 px-4">Shift</th>
                    <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 text-left py-3 px-4">Time</th>
                    <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 text-left py-3 px-4 hidden sm:table-cell">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200">
                  {schedule.entries.map((entry, idx) => (
                    <tr key={entry.entry_id || `${entry.date}-${entry.shift_id}-${idx}`} className="hover:bg-zinc-50 transition-colors">
                      <td className="py-3 px-4 text-sm tabular-nums text-zinc-900">{fmtDateFull(entry.date)}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: entry.color }} />
                          <span className="text-sm font-medium text-zinc-900">{entry.shift_name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5 text-sm tabular-nums text-zinc-600">
                          <Clock className="w-3.5 h-3.5 text-zinc-400" />
                          {fmtTime(entry.start_time)} — {fmtTime(entry.end_time)}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-zinc-500 hidden sm:table-cell">{entry.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}

        {/* Leave Requests */}
        <div>
          <h2 className="text-lg tracking-tight font-bold text-zinc-900 mb-4">My Leave Requests</h2>
          {leaveRequests.length === 0 ? (
            <div className="bg-white border border-zinc-200 p-8 text-center">
              <p className="text-sm text-zinc-500">No leave requests yet</p>
            </div>
          ) : (
            <div className="bg-white border border-zinc-200 overflow-hidden">
              <div className="divide-y divide-zinc-200">
                {leaveRequests.map((req) => (
                  <div key={req.request_id} className="p-4 flex items-center justify-between" data-testid={`leave-${req.request_id}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-zinc-900">
                          {leaveTypeLabels[req.leave_type] || req.leave_type}
                        </span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded capitalize ${getStatusBadge(req.status)}`}>
                          {req.status}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {fmtRange(req.start_date, req.end_date)}
                        {req.reason && ` | ${req.reason}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Leave Request Dialog */}
      <Dialog open={leaveDialog} onOpenChange={setLeaveDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg tracking-tight font-bold">Request Leave</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitLeave} className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 items-start">
            <div>
              <Label className="text-xs uppercase tracking-wide text-zinc-500">Leave Type *</Label>
              <Select value={leaveForm.leave_type}
                onValueChange={(v) => setLeaveForm(p => ({ ...p, leave_type: v }))}>
                <SelectTrigger className="mt-1" data-testid="leave-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rostered_day_off">Rostered Day Off</SelectItem>
                  <SelectItem value="annual_leave">Annual Leave</SelectItem>
                  <SelectItem value="sick_leave">Sick Leave</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wide text-zinc-500">Start Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full mt-1 justify-start text-left font-normal"
                      data-testid="leave-start-date">
                      {leaveForm.start_date ? fmtDateMed(leaveForm.start_date) : "Pick date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarPicker mode="single" selected={leaveForm.start_date}
                      onSelect={(d) => setLeaveForm(p => ({ ...p, start_date: d }))} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-zinc-500">End Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full mt-1 justify-start text-left font-normal"
                      data-testid="leave-end-date">
                      {leaveForm.end_date ? fmtDateMed(leaveForm.end_date) : "Pick date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarPicker mode="single" selected={leaveForm.end_date}
                      onSelect={(d) => setLeaveForm(p => ({ ...p, end_date: d }))} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-zinc-500">Reason</Label>
              <Textarea value={leaveForm.reason}
                onChange={(e) => setLeaveForm(p => ({ ...p, reason: e.target.value }))}
                placeholder="Optional reason..." className="mt-1" rows={2}
                data-testid="leave-reason-input" />
            </div>
            <div className="flex justify-end gap-3 pt-2 sm:col-span-2">
              <Button type="button" variant="outline" onClick={() => setLeaveDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white" data-testid="submit-leave-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Request
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffDashboard;
